import { Navigate, useLocation, type RouteObject } from 'react-router-dom';
import { BrandSetupPage } from '../../pages/BrandSetupPage';
import { WorkspaceCreatePage } from '../../pages/WorkspaceCreatePage';
import { WorkspaceEditPage } from '../../pages/WorkspaceEditPage';
import { WorkspaceFormPage } from '../../pages/WorkspaceFormPage';
import { BrandRoute } from '../brand/BrandRoute';
import { WORKSPACE_EDIT_PATH, type EditPageKey } from '../brand/paths';
import { APPROVALS_ANCHOR } from '../testMode/testMode';

/**
 * The Brand Workspace routes (web.md §2.2, final-design §2.14). Each one picks today's page or its Brand
 * Workspace v2 page by the install's flag, through one BrandRoute (loading, then the page; an error with
 * Try again when the flag cannot be read):
 *
 * - `/workspace/new`: today's WorkspaceFormPage (create), or WorkspaceCreatePage.
 * - `/workspace/edit`: today's WorkspaceFormPage (edit), or on to a v2 page (EditIndexRedirect).
 * - `/workspace/edit/:page`: back to `/workspace/edit` with the flag off, or WorkspaceEditPage (which
 *   sends an unknown page, and Danger zone for a member, to Brand information).
 * - `/workspace/setup`: back to `/workspace/edit` with the flag off, or BrandSetupPage, the wizard.
 *
 * Every redirect replaces its history entry, so Back never lands on it, and keeps the query, whose `?ws`
 * names the workspace. router.tsx mounts the first three inside StandardLayout and the wizard beside the
 * other full-height pages (E23); the route spec mounts them without the rest of the app.
 */

/**
 * `/workspace/edit` with Brand Workspace v2, which has no page of its own: Brand information, or the
 * Workflow page for a link to the Approvals section (`#approvals`: today's test-mode link and bookmarks).
 */
function EditIndexRedirect() {
  const { search, hash } = useLocation();
  const approvals = hash === `#${APPROVALS_ANCHOR}`;
  const page: EditPageKey = approvals ? 'workflow' : 'brand';
  return <Navigate to={{ pathname: `${WORKSPACE_EDIT_PATH}/${page}`, search, hash: approvals ? hash : '' }} replace />;
}

/**
 * A v2 path while the flag is off: today's Edit page, for the same workspace. An Edit page's hash names
 * a section, which today's page may have too (`#approvals`), so it is kept; the wizard's is not.
 */
function TodaysEditPage({ keepHash }: { keepHash: boolean }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: WORKSPACE_EDIT_PATH, search, hash: keepHash ? hash : '' }} replace />;
}

/** Inside StandardLayout. */
export const brandStandardRoutes: RouteObject[] = [
  {
    path: 'workspace/new',
    element: <BrandRoute v1={<WorkspaceFormPage mode="create" />} v2={<WorkspaceCreatePage />} />,
  },
  {
    path: 'workspace/edit',
    element: <BrandRoute v1={<WorkspaceFormPage mode="edit" />} v2={<EditIndexRedirect />} />,
  },
  {
    path: 'workspace/edit/:page',
    element: <BrandRoute v1={<TodaysEditPage keepHash />} v2={<WorkspaceEditPage />} />,
  },
];

/** Full height, outside StandardLayout: the wizard's bar stays at the bottom of the window (E23). */
export const brandFullHeightRoutes: RouteObject[] = [
  {
    path: 'workspace/setup',
    element: <BrandRoute v1={<TodaysEditPage keepHash={false} />} v2={<BrandSetupPage />} />,
  },
];
